import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices, useBranding, useClient } from '@utils/api';
import { Service } from '@utils/api/aboboApiTypes';
import { formatDuration } from '@utils/helpers';
import { ErrorLanding } from '@components/ErrorLanding';
import { useBookingContext } from '@contexts/BookingContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { VStack } from '../ui';

type ServiceSelectionStepProps = {
  preSelectedService?: Service;
};

const ServiceSelectionStep: FC<ServiceSelectionStepProps> = ({ preSelectedService }) => {
  const { t, i18n } = useTranslation();
  const { services, isError: isServicesError } = useServices({
    language: i18n.language
  });
  const { branding } = useBranding();
  const { client } = useClient();
  const { updateBookingData, goForward } = useBookingContext();
  const hasAutoSelected = useRef(false);

  const getDurationDisplay = (duration: number) => {
    if (duration < 60) {
      return t('service.duration', { serviceDuration: duration });
    }
    
    const { hours, minutes } = formatDuration(duration);
    return t('service.durationHoursMinutes', { hours, minutes });
  };

  const handleServiceSelect = useCallback((selectedService: Service) => {
    // Update booking context with selected service ID
    updateBookingData({
      selectedServiceId: selectedService.id,
      appointmentsAttributes: [{
        scheduledAt: '',
        operatorId: '',
        serviceId: selectedService.id,
      }]
    });

    // Navigate to next step based on service requirements
    if (selectedService.screenFormId) {
      goForward('screeningForm');
    } else if (selectedService.appointmentTimeRequired) {
      goForward('appointmentSelection');
    } else {
      goForward('patientInformation');
    }
  }, [updateBookingData, goForward]);

  // Auto-select if a service was pre-selected via query param
  useEffect(() => {
    if (preSelectedService && branding?.name && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      handleServiceSelect(preSelectedService);
    }
  }, [preSelectedService, branding?.name, handleServiceSelect]);

  // Auto-select if only one service is available (single-service auto-skip)
  useEffect(() => {
    if (
      services &&
      services.length === 1 &&
      !preSelectedService &&
      branding?.name &&
      !hasAutoSelected.current
    ) {
      hasAutoSelected.current = true;
      const singleService = services[0];

      // Update booking context with selected service ID
      updateBookingData({
        selectedServiceId: singleService.id,
        appointmentsAttributes: [{
          scheduledAt: '',
          operatorId: '',
          serviceId: singleService.id,
        }]
      });

      // Navigate based on service requirements
      if (singleService.screenFormId) {
        goForward('screeningForm');
      } else if (singleService.appointmentTimeRequired) {
        goForward('appointmentSelection');
      } else {
        goForward('patientInformation');
      }
    }
  }, [services, preSelectedService, branding?.name, updateBookingData, goForward]);

  if (isServicesError) {
    return (
      <ErrorLanding 
        heading={t('errorPage.appointment.heading')} 
        subHeading={t('errorPage.appointment.subheading')} 
        description={t('errorPage.appointment.description')} 
        footer={t('errorPage.appointment.subheading')} 
      />
    );
  }

  if (!branding || !client) return null;

  return (
    <div className="">
      <div className="py-2 space-y-4" role="group" aria-label={t('aria.serviceFirstStep')}>
        <Skeleton className={!services ? "h-36" : "hidden"}>
          <div className="h-36" />
        </Skeleton>
        {services && (
          <VStack className='w-full max-w-sm mx-auto'>
            {services.map((service) => {
              return (
                <Button
                  className="h-auto min-h-10 py-2 justify-center w-full"
                  key={service.id}
                  onClick={() => handleServiceSelect(service)}
                  type="button"
                  aria-label={t('aria.serviceSelect', {
                    serviceName: service.name,
                    serviceDuration: service.duration
                  })}
                >
                  <div className="flex flex-col gap-0 w-full min-w-0" style={{ color: branding.fontColor }} aria-hidden="true">
                    <div className="font-bold text-center truncate">
                      {service.name}
                    </div>
                    {service.appointmentTimeRequired && (
                      <span className="text-sm"> 
                        {getDurationDisplay(service.duration)} 
                      </span>
                    )}
                  </div>
                </Button>
              );
            })}
          </VStack>
        )}
      </div>
      <Separator aria-hidden="true" className="my-4"/>
      <p className="text-sm pt-3">
        {t('booking.footer.confirm')}
        <a 
          className="text-teal-700 hover:underline" 
          target='_blank' 
          href={`https://${client?.slug}.myclinically.com/privacy`} 
          aria-label={t('aria.terms')}
        >
          {t('booking.footer.terms')}
        </a>
        {" "} {t('booking.footer.and')} {" "}
        <a 
          className="text-teal-700 hover:underline" 
          target='_blank' 
          href={`https://${client?.slug}.myclinically.com/privacy`} 
          aria-label={t('aria.privacy')}
        >
          {t('booking.footer.privacy')}
        </a>
      </p>  
    </div>
  );
};

export default ServiceSelectionStep;
